/**
 * A handle's avatar -- Task 5.6, designed at 5.4a (Settled Decisions).
 *
 * **Nothing here ever sees a `User`, and that is the whole point.** Flarum's
 * `Avatar` derives its image, `alt`, `title`, `aria-label`, its fallback initial
 * and its background colour from the `User` it is handed. Task 5.4 could
 * override two of those from outside and no more, which left the *image* still
 * being that person's real, stable upload -- the same picture in every Issue,
 * and therefore a perfect cross-Issue correlator sitting beside a pseudonym.
 * The fix is not to sanitize that component's inputs; it is to stop calling it.
 *
 * Everything below is derived from the handle STRING, which the payload already
 * carries. That has a pleasant consequence: this needed no new API field, and
 * it cannot leak, because there is nothing else in scope to leak.
 *
 * FOUR INITIALS, NOT ONE. The word list holds 20 words with only **12 distinct
 * first letters**, four of them `c` -- so `copper-…`, `canyon-…`, `candle-…`
 * and `cotton-…` would all wear the same badge. Four letters make the monogram
 * as distinctive as the handle is.
 */

/**
 * Fixed so contrast holds in both themes; only the hue varies.
 *
 * **30% is measured, not chosen.** White text over `hsl(h, 55%, L)` was
 * computed against WCAG relative luminance for every hue from 0 to 359: the
 * worst case is hue 60 (yellow), where green's weight in the luminance formula
 * dominates. At the 38% first written here that hue gives **3.13:1**, which
 * fails AA; 34% gives 3.83:1 and 32% gives 4.26:1, both still failing. 30% is
 * the first that clears 4.5:1 everywhere, at 4.75:1.
 *
 * Pinning the lightness rather than theming it is what lets the text colour
 * below be a constant, and is why the badge reads the same in light and dark.
 */
const SATURATION = 55;
const LIGHTNESS = 30;
export default function HandleAvatar(handle, attrs) {
  if (attrs === void 0) {
    attrs = {};
  }
  const initials = initialsOf(handle);
  return m("span", {
    className: ['Avatar', 'IdeaSynqAvatar', attrs.self ? 'IdeaSynqAvatar--self' : '', attrs.className || ''].filter(Boolean).join(' '),
    style: {
      background: `hsl(${hueOf(handle)}, ${SATURATION}%, ${LIGHTNESS}%)`
    },
    role: "img",
    "aria-label": handle,
    title: handle,
    "data-initials": initials
  }, initials);
}

/**
 * `copper-willow-marble-indigo` → `CWMI`.
 *
 * Falls back to whatever the string starts with rather than throwing, because
 * the placeholder for an unresolved contributor (`[unknown participant]`) is not
 * four hyphenated words and still has to draw something.
 */
export function initialsOf(handle) {
  const letters = handle.split('-').map(word => word.trim()[0]).filter(letter => Boolean(letter) && /[a-z0-9]/i.test(letter));
  if (!letters.length) return '?';
  return letters.slice(0, 4).join('').toUpperCase();
}

/**
 * A stable hue for a handle, in [0, 360).
 *
 * djb2 followed by an avalanche mix. Deriving the colour rather than storing it
 * means no schema change (Invariant 3 forbids mutating a handle row in any
 * case), the same answer on every server and every render, and a value a test
 * can predict.
 *
 * THE MIX IS NOT CEREMONY. Handles are near-identical strings -- same word
 * list, same length, same shape -- and plain djb2 over them clustered badly:
 * the eight handles in the dev forum all landed between hue 74 and 219, so
 * every badge was some shade of green or blue. The avalanche spreads the same
 * eight across 0-297.
 *
 * WHAT IT STILL DOES NOT PROMISE is that two handles look different. Eight
 * values in 360 slots collide by birthday paradox long before the hash is at
 * fault, and a wider list makes that likelier, not less. **The four initials
 * are the distinguisher; the colour is decoration that happens to help.** Any
 * future attempt to guarantee distinct colours has to assign them per Issue
 * with knowledge of the others, which is a different and much more expensive
 * design.
 */
export function hueOf(handle) {
  let hash = 5381;
  for (let i = 0; i < handle.length; i++) {
    hash = (hash << 5) + hash + handle.charCodeAt(i) | 0;
  }

  // Math.imul, because `*` on two large ints silently leaves 32-bit space and
  // loses the low bits that carry the entropy.
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x7feb352d);
  hash ^= hash >>> 15;
  hash = Math.imul(hash, 0x846ca68b);
  hash ^= hash >>> 16;
  return Math.abs(hash) % 360;
}